/**
 * UserManagementTab — Unified Pending / Approved / Denied
 * Single API call per action. No double-firing on mount.
 */
import React, { useEffect, useState, useRef } from 'react';
import apiFetch from '@wordpress/api-fetch';
import { Button } from '../../../components/ui/index.jsx';
import { useToast } from '../../../components/ui/Toast.jsx';

const wpAdminUrl = (path) => {
  const { origin, pathname } = window.location;
  const match = pathname.match(/^(.*?)(\/wp-admin|\/wp-login|\/wp-content)/);
  return `${origin}${match ? match[1] : ''}/wp-admin/${path}`;
};

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const TABS = [
  { id: 'pending',  label: 'Pending',  icon: '⏳', status: 'not_confirm_approve', activeClass: 'is-active-pending',  badge: '--pending',  emptyIcon: '🎉', emptyText: 'No pending users',  emptySub: 'All registrations have been reviewed' },
  { id: 'approved', label: 'Approved', icon: '✅', status: 'confirm_approve',     activeClass: 'is-active-approved', badge: '--approved', emptyIcon: '👥', emptyText: 'No approved users', emptySub: 'Approved users will appear here' },
  { id: 'denied',   label: 'Denied',   icon: '🚫', status: 'denied_user',         activeClass: 'is-active-denied',   badge: '--denied',   emptyIcon: '🎉', emptyText: 'No denied users',   emptySub: 'No registrations have been rejected' },
];

/* ─────────────────────────────────────────────────────────── 
   UserTable
   SINGLE useEffect drives ALL fetches.
   State is stored in a ref so the effect can read current values
   without adding them to the dependency array (which would cause
   extra fetches when the API response sets state back).
──────────────────────────────────────────────────────────── */
const UserTable = ({ tab, onCountsRefresh }) => {
  const toast = useToast();

  const [users,      setUsers]   = useState([]);
  const [loading,    setLoading] = useState(false);
  const [page,       setPage]    = useState(1);
  const [totalUsers, setTotal]   = useState(0);
  const [totalPages, setPages]   = useState(1);
  const [perPage,    setPerPage] = useState(10);

  // fetchTrigger increments every time we want a fetch.
  // This is the ONLY thing in the useEffect dependency array.
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Keep a ref of current page/perPage so the fetch closure always
  // reads fresh values without being in the dep array.
  const paramsRef = useRef({ page: 1, perPage: 10 });

  const triggerFetch = (newPage, newPerPage) => {
    paramsRef.current = { page: newPage, perPage: newPerPage };
    setFetchTrigger(n => n + 1);
  };

  // THE ONLY useEffect — fires exactly when fetchTrigger increments
  useEffect(() => {
    let cancelled = false;
    const { page: p, perPage: pp } = paramsRef.current;

    (async () => {
      try {
        setLoading(true);

        // First, refresh global counts (must succeed before fetching users)
        if (typeof onCountsRefresh === 'function') {
          await onCountsRefresh();
          if (cancelled) return;
        }

        const res = await apiFetch({
          path: `/bpsfw/v1/users?status=${tab.status}&page=${p}&per_page=${pp}&nocache=${Date.now()}`,
        });
        if (cancelled) return;
        setUsers(res.users || []);
        setPage(res.page);
        setPerPage(res.per_page);
        setPages(res.totalPages);
        setTotal(res.totalUsers);
      } catch (err) {
        if (!cancelled) console.error('Fetch users error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fetchTrigger]); // ← only this, no page/perPage/tab.id

  // Mount: trigger initial fetch once
  useEffect(() => {
    triggerFetch(1, 10);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (next) => {
    setPage(next);
    triggerFetch(next, perPage);
  };

  const handlePerPageChange = (next) => {
    setPage(1);
    setPerPage(next);
    triggerFetch(1, next);
  };

  const updateStatus = async (userId, status, msg) => {
    const confirmed = await toast.confirm(msg);
    if (!confirmed) return;
    try {
      setLoading(true);
      await apiFetch({ path: '/bpsfw/v1/update-approval', method: 'POST', data: { user_id: userId, status } });
      toast.success('User status updated.');
    } catch (err) {
      console.error('Status update error:', err);
      toast.error('Failed to update user status.');
    }
    // Refresh global counts first, then reload the table
    try {
      if (typeof onCountsRefresh === 'function') await onCountsRefresh();
    } catch (err) {
      // ignore count refresh errors — we'll still reload the table
      console.error('Counts refresh error:', err);
    }
    triggerFetch(page, perPage);
  };

  return (
    <div>
      {loading && (
        <div className="bpsfw-overlay-loader">
          <div className="bpsfw-loader-box">
            <div className="bpsfw-spinner" />
            <span className="bpsfw-loader-text">Loading…</span>
          </div>
        </div>
      )}

      <div className="bui-table-header">
        <div className="bui-table-header__count">
          {totalUsers} user{totalUsers !== 1 ? 's' : ''} &nbsp;·&nbsp; page {page} of {totalPages || 1}
        </div>
        <div className="bui-per-page">
          <span>Rows per page</span>
          <select value={perPage} onChange={e => handlePerPageChange(+e.target.value)}>
            {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="bui-user-table-wrap">
        <table className="bui-user-table">
          <thead>
            <tr><th>User</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr className="bui-empty-row">
                <td colSpan={5}>
                  <div className="bui-empty-state">
                    <div className="bui-empty-state__icon">{tab.emptyIcon}</div>
                    <div className="bui-empty-state__text">{tab.emptyText}</div>
                    <div className="bui-empty-state__sub">{tab.emptySub}</div>
                  </div>
                </td>
              </tr>
            ) : users.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="bui-user-cell">
                    <img src={user.avatar_url} alt={user.name} className="bui-user-avatar" />
                    <a href={wpAdminUrl(`user-edit.php?user_id=${user.id}`)} className="bui-user-link">{user.name}</a>
                  </div>
                </td>
                <td><a href={`mailto:${user.email}`} className="bui-email-link">{user.email}</a></td>
                <td><span className="bui-role-tag">{Array.isArray(user.roles) ? user.roles.join(', ') : '—'}</span></td>
                <td><span className={`bui-status-badge bui-status-badge${tab.badge}`}>{tab.label}</span></td>
                <td>
                  <div className="bui-action-cell">
                    {tab.id === 'pending' && (<>
                      <Button variant="primary" size="sm" onClick={() => updateStatus(user.id, 'confirm_approve', 'Approve this user?')}>Approve</Button>
                      <Button variant="danger"  size="sm" onClick={() => updateStatus(user.id, 'denied_user',     'Deny this user?')}>Deny</Button>
                    </>)}
                    {tab.id === 'approved' && (
                      <Button variant="danger" size="sm" onClick={() => updateStatus(user.id, 'denied_user', 'Revoke access for this user?')}>Revoke</Button>
                    )}
                    {tab.id === 'denied' && (
                      <Button variant="primary" size="sm" onClick={() => updateStatus(user.id, 'confirm_approve', 'Re-approve this user?')}>Re-approve</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bui-pagination">
        <div className="bui-pagination__controls">
          <button className="bui-page-btn" disabled={page <= 1 || loading} onClick={() => handlePageChange(page - 1)}>← Prev</button>
          <span className="bui-pagination__page-label">{page} / {totalPages || 1}</span>
          <button className="bui-page-btn" disabled={page >= totalPages || !totalPages || loading} onClick={() => handlePageChange(page + 1)}>Next →</button>
        </div>
        <div className="bui-pagination__info">{totalUsers} total</div>
      </div>
    </div>
  );
};

/* ─── Main ────────────────────────────────────────────────── */
const UserManagementTab = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [counts, setCounts]       = useState({ pending: '—', approved: '—', denied: '—' });
  const current = TABS.find(t => t.id === activeTab);
  // Deduplicated low-level fetch helper — returns raw response or throws.
  // If a counts request is already in-flight, return the same promise so
  // multiple callers share the result instead of triggering duplicate calls.
  const countsFetchPromiseRef = useRef(null);
  const fetchCountsRaw = () => {
    if (countsFetchPromiseRef.current) return countsFetchPromiseRef.current;
    const p = apiFetch({ path: `/bpsfw/v1/user-counts?nocache=${Date.now()}` })
      .then((res) => {
        countsFetchPromiseRef.current = null;
        return res;
      })
      .catch((err) => {
        countsFetchPromiseRef.current = null;
        throw err;
      });
    countsFetchPromiseRef.current = p;
    return p;
  };

  // refreshCounts: fetch and update `counts`. Errors propagate to caller.
  const refreshCounts = async () => {
    const res = await fetchCountsRaw();
    if (res) {
      setCounts({
        pending: typeof res.pending === 'number' ? res.pending : res.pending || '—',
        approved: typeof res.approved === 'number' ? res.approved : res.approved || '—',
        denied: typeof res.denied === 'number' ? res.denied : res.denied || '—',
      });
    }
    return res;
  };

  // Fetch all counts in a single request on mount to avoid multiple
  // simultaneous calls. Falls back to per-tab lazy fetch if needed.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetchCountsRaw();
        if (!cancelled && res) {
          setCounts({
            pending: typeof res.pending === 'number' ? res.pending : res.pending || '—',
            approved: typeof res.approved === 'number' ? res.approved : res.approved || '—',
            denied: typeof res.denied === 'number' ? res.denied : res.denied || '—',
          });
        }
      } catch (err) {
        // noop: we'll lazily fetch counts when user clicks each tab or on actions
      }
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bpsfw-users-wrap">
      <div className="bpsfw-stats-row">
        {TABS.map(tab => (
          <div
            key={tab.id}
            className={['bpsfw-stat-card', activeTab === tab.id ? tab.activeClass : ''].filter(Boolean).join(' ')}
            onClick={() => setActiveTab(tab.id)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setActiveTab(tab.id)}
          >
            <div className="bpsfw-stat-icon">{tab.icon}</div>
            <div>
              <div className="bpsfw-stat-num">{counts[tab.id]}</div>
              <div className="bpsfw-stat-label">{tab.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bpsfw-section-card">
        <div className="bpsfw-section-header">
          <div className="bpsfw-section-icon">{current.icon}</div>
          <div>
            <div className="bpsfw-section-title">{current.label} Users</div>
            <div className="bpsfw-section-subtitle">{current.emptySub}</div>
          </div>
          <span className="bpsfw-role-badge">{counts[current.id]}</span>
        </div>
        <div className="bpsfw-section-body">
          {/* key= remounts UserTable cleanly when tab changes, resetting all state */}
          <UserTable key={activeTab} tab={current} onCountsRefresh={refreshCounts} />
        </div>
      </div>
    </div>
  );
};

export default UserManagementTab;
